import { useState } from 'react';
import { Layout } from '../components/Layout';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Download, FileText, CheckCircle, AlertCircle, Info, Copy, Sheet, Database } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import { exportToGoogleSheets } from '../services/googleSheetsService';

interface ParsedResult {
  success: boolean;
  stats: {
    totalEntries: number;
    fieldsDetected: number;
  };
  fields: string[];
  preview: Array<Record<string, string>>;
  data: Array<Record<string, string>>;
  csv: string;
  filename: string;
}

export default function DataParser() {
  const [inputText, setInputText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<ParsedResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Save states
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveAction, setSaveAction] = useState<'sheets' | 'database' | null>(null);

  // Options
  const [delimiter, setDelimiter] = useState(',');
  const [includeHeaders, setIncludeHeaders] = useState(true);

  const handleParse = async () => {
    if (!inputText.trim()) {
      setError('Please paste some data first');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Send as JSON instead of FormData
      const response = await fetch(`${API_BASE_URL}/parse-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rawData: inputText,
          delimiter,
          includeHeaders,
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to parse data');
      }

      const data: ParsedResult = await response.json();
      setResult(data);
      setSuccessMessage(`Successfully parsed ${data.stats.totalEntries} entries with ${data.stats.fieldsDetected} fields!`);
    } catch (err: any) {
      console.error('Parse error:', err);
      setError(err.message || 'Failed to parse data');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;

    const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', result.filename || 'parsed-data.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClear = () => {
    setInputText('');
    setResult(null);
    setError(null);
    setSuccessMessage(null);
  };

  const handleSaveToSheets = () => {
    setSaveAction('sheets');
    setShowSaveModal(true);
    setSaveName(`Parsed Data - ${new Date().toLocaleDateString()}`);
  };

  const handleSaveToDatabase = () => {
    setSaveAction('database');
    setShowSaveModal(true);
    setSaveName(`Parsed Data - ${new Date().toLocaleDateString()}`);
  };

  const handleConfirmSave = async () => {
    if (!result || !saveName.trim() || !saveAction) return;

    setIsSaving(true);
    setError(null);

    try {
      if (saveAction === 'sheets') {
        // Export to Google Sheets
        await exportToGoogleSheets(result.data, saveName, result.fields);
        setSuccessMessage(`Successfully exported to Google Sheets: ${saveName}`);
      } else if (saveAction === 'database') {
        // Save to database
        const response = await fetch(`${API_BASE_URL}/saved-searches`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            name: saveName,
            queryText: 'Parsed Data',
            results: result.data,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to save to database');
        }

        setSuccessMessage(`Successfully saved to database: ${saveName}`);
      }

      setShowSaveModal(false);
      setSaveName('');
      setSaveAction(null);
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.message || `Failed to save to ${saveAction === 'sheets' ? 'Google Sheets' : 'database'}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Data Parser</h1>
          <p className="text-gray-600">
            Paste unorganized CSV or text data and convert it into clean, structured CSV format
          </p>
        </div>

        {/* Info Box */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-2">Automatically detects and extracts:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Business names, emails, phone numbers, websites</li>
                <li>Addresses, counties, states, zip codes</li>
                <li>Social media links (Facebook, Instagram, Twitter)</li>
                <li>License IDs, dates, and custom patterns</li>
              </ul>
              <p className="mt-2 text-xs">
                Supports: Semi-structured text, messy CSVs, labeled data, multi-line entries
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Input */}
          <div>
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Paste Your Data</h2>
                {inputText && (
                  <button
                    onClick={handleClear}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Text Area */}
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder='Paste your unorganized CSV or text data here...\n\nExample:\n"Business A - Phone: (555) 123-4567"\n"Business B - Address: 123 Main St"'
                className="w-full h-96 px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm text-black"
              />

              <div className="mt-4 text-sm text-gray-500">
                {inputText.length > 0 && (
                  <p>{inputText.length.toLocaleString()} characters • {inputText.split('\n').length} lines</p>
                )}
              </div>

              {/* Options */}
              <div className="space-y-4 mt-6">
                <h3 className="text-lg font-semibold text-gray-900">Options</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Output Delimiter
                  </label>
                  <select
                    value={delimiter}
                    onChange={(e) => setDelimiter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value=",">Comma (,)</option>
                    <option value=";">Semicolon (;)</option>
                    <option value="\t">Tab</option>
                    <option value="|">Pipe (|)</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="include-headers"
                    checked={includeHeaders}
                    onChange={(e) => setIncludeHeaders(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="include-headers" className="ml-2 text-sm text-gray-700">
                    Include header row in output
                  </label>
                </div>
              </div>

              {/* Parse Button */}
              <button
                onClick={handleParse}
                disabled={!inputText.trim() || isProcessing}
                className="w-full mt-6 bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {isProcessing ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FileText className="h-5 w-5 mr-2" />
                    Parse Data
                  </>
                )}
              </button>
            </div>

            {/* Messages */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            {successMessage && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-sm text-green-800">{successMessage}</p>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Results */}
          <div>
            {result && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">Results</h2>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleDownload}
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download CSV
                    </button>

                    <button
                      onClick={handleSaveToSheets}
                      disabled={isSaving}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-300 transition-colors flex items-center"
                    >
                      <Sheet className="h-4 w-4 mr-2" />
                      Save to Google Sheets
                    </button>

                    <button
                      onClick={handleSaveToDatabase}
                      disabled={isSaving}
                      className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:bg-gray-300 transition-colors flex items-center"
                    >
                      <Database className="h-4 w-4 mr-2" />
                      Save for Enrichment
                    </button>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-blue-600 font-medium">Total Entries</p>
                    <p className="text-2xl font-bold text-blue-900">{result.stats.totalEntries}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-green-600 font-medium">Fields Detected</p>
                    <p className="text-2xl font-bold text-green-900">{result.stats.fieldsDetected}</p>
                  </div>
                </div>

                {/* Fields */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Detected Fields:</h3>
                  <div className="flex flex-wrap gap-2">
                    {result.fields.map((field, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                      >
                        {field}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Preview */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Preview (First 10 rows):</h3>
                  <div className="overflow-auto max-h-96 border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          {result.fields.map((field, index) => (
                            <th
                              key={index}
                              className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
                            >
                              {field}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {result.preview.map((row, rowIndex) => (
                          <tr key={rowIndex} className="hover:bg-gray-50">
                            {result.fields.map((field, fieldIndex) => (
                              <td
                                key={fieldIndex}
                                className="px-4 py-2 text-sm text-gray-900 max-w-xs truncate"
                                title={row[field]}
                              >
                                {row[field] || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {!result && !isProcessing && (
              <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
                <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Paste and parse data to see results</p>
              </div>
            )}
          </div>
        </div>

        {/* Save Modal */}
        {showSaveModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {saveAction === 'sheets' ? 'Save to Google Sheets' : 'Save for Enrichment'}
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-black"
                  placeholder="Enter a name..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowSaveModal(false);
                    setSaveName('');
                    setSaveAction(null);
                  }}
                  disabled={isSaving}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSave}
                  disabled={isSaving || !saveName.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 flex items-center"
                >
                  {isSaving ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
